import React, { useEffect } from 'react';
import {Outlet, useNavigate, useParams, useLocation} from 'react-router-dom';
import {KioskProvider, useKiosk} from '../../contexts/KioskContext';
import paths from '../../routes/paths';
import WbLoader from '../../components/common/WbLoader';
import KioskLayout from '../../components/kiosk/KioskLayout';
import {getWelbeeHomeUrl, schoolHasUrn} from '../../utils/kioskAccess';

// Inner component that uses the context and handles routing logic
function KioskRoutesHandler() {
  const { schoolSlug } = useParams();
  const location = useLocation();
  const { school, loading, mode, error, urnVerified } = useKiosk();
  const navigate = useNavigate();

  // A school with no URN on record can't run a kiosk — the URN is the only identity check on an
  // unauthenticated device. Once loaded without one, the kiosk is unavailable (we redirect home).
  const kioskUnavailable = !loading && !error && !!school && !schoolHasUrn(school);

  useEffect(() => {
    if (loading || error || !school) return;

    // No URN -> kiosk not allowed; leave the app for Welbee home (same as the exit-kiosk action).
    if (!schoolHasUrn(school)) {
      window.location.href = getWelbeeHomeUrl();
      return;
    }

    const currentPath = location.pathname;
    const isOnUrnScreen = currentPath.includes('/urn');
    const isRootPath = currentPath === `/kiosk/${schoolSlug}`;

    // Mandatory URN gate: every kiosk must verify the school URN before reaching anything else.
    // Once verified (flag persisted in the session) the gate never fires again.
    if (!urnVerified && !isOnUrnScreen) {
      navigate(paths.kioskUrn.replace(':schoolSlug', schoolSlug));
      return;
    }

    // On the bare mode-selection URL with a mode already set -> jump to that mode's welcome
    // (a configured kiosk goes straight into its flow). When no mode is set, the bare URL
    // renders the mode picker via the index route. The change-mode gesture (triple-tap logo)
    // resets the mode first, so it lands back on the picker here instead of redirecting away.
    if (isRootPath && mode) {
      if (mode === 'Parent') {
        navigate(paths.kioskParentWelcome.replace(':schoolSlug', schoolSlug));
      } else if (mode === 'Student') {
        navigate(paths.kioskStudentWelcome.replace(':schoolSlug', schoolSlug));
      }
      return;
    }
  }, [loading, school, error, mode, urnVerified, navigate, schoolSlug, location.pathname]);

  // Show the loader while loading, and while a no-URN school is being redirected home (avoids a
  // flash of the mode picker before window.location navigates away).
  if (loading || kioskUnavailable) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  if (error) {
    return (
      <KioskLayout>
        <div className="kiosk-error" style={{ textAlign: 'center' }}>
          <h2>School Not Found</h2>
          <p>{error.detail || error.message || 'The school could not be found. Please check the URL.'}</p>
        </div>
      </KioskLayout>
    );
  }

  return <Outlet />;
}

// Main component with provider
export default function KioskApp() {
  return (
    <KioskProvider>
      <KioskRoutesHandler />
    </KioskProvider>
  );
}
